import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Grid, Typography } from "@material-ui/core";
import GoogleMapReact from "google-map-react";
import * as API_KEYS from "../../constants/apiKeys";
import React, { useEffect, useState } from "react";
import { LocationOn } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { orange, red } from "@material-ui/core/colors";
import DeviceStatus from "../../components/DeviceStatusModal/DeviceStatus";
import DeviceStatusOthers from "../../components/DeviceStatusModal/DeviceStatusOthers";
import LoadingPage from "../LoadingPage";
import BuildIcon from "@material-ui/icons/Build";
import DeviceTrackingStatusMapper from "../../functions/DeviceTrackingStatusMapper";
import * as CONF from "../../constants/conf";
import * as DATANETAPI from "../DataNet/DataNet";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { EMT_VIEWER } from "../../constants/roles";
import { hasPermission } from "../../functions/authorization";
import { getDeviceDisplayName } from "../../functions/getDeviceDisplayName";

const DeviceStatusModal = ({
    isStatusModalOpen,
    device,
    setisStatusModalOpen,
    onTrackingStatusUpdated,
    refreshDevices,
    authUser,
    autoOpenOperationalGis,
    onOperationalGisAutoOpened,
}) => {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery("(max-width:420px)");
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [resolvingIncident, setResolvingIncident] = useState(false);
    const [currentDevice, setCurrentDevice] = useState(device);
    const statusText = DeviceTrackingStatusMapper.toTrackerStatus(currentDevice?.tracker_status);
    const [showResolvedSnackbar, setShowResolvedSnackbar] = useState(false);
    const isClosableOperationalIncidentStatus =
        currentDevice?.operational_incident_status === CONF.DEVICE_OPERATIONAL_INCIDENT_STATUS_ACTIVE ||
        currentDevice?.operational_incident_status === CONF.DEVICE_OPERATIONAL_INCIDENT_STATUS_EXTRACTED ||
        currentDevice?.operational_incident_status === CONF.DEVICE_OPERATIONAL_INCIDENT_STATUS_MAINTENANCE;

    const userCanConfirmOperationalClosure = !hasPermission(authUser, EMT_VIEWER);

    const showOperationalClosure =
        currentDevice?.program_type === CONF.DEVICE_PROGRAM_TYPE_EMT &&
        isClosableOperationalIncidentStatus &&
        userCanConfirmOperationalClosure;

    const showResolveIncident = showOperationalClosure;

    const resolveActionI18nKey = showOperationalClosure
        ? "devices.tracking.config.status.operational_closure_action"
        : "devices.tracking.config.status.resolve_action";

    const handleConfirmOperationalClosure = async () => {
        setResolvingIncident(true);
        try {
            const res = await DATANETAPI.confirmTrackerOperationalIncidentClosure(currentDevice.id);
            await new Promise(resolve => setTimeout(resolve, 1500));

            if (res?.success) {
                setCurrentDevice(prev => ({
                    ...prev,
                    operational_incident_status: res.operational_incident_status
                }));

                onTrackingStatusUpdated(
                    currentDevice.id,
                    undefined,
                    res.operational_incident_status
                );

                await handleRefreshCurrentDevice({ silent: true });
                await refreshDevices?.();

                setResolveModalOpen(false);
                setisStatusModalOpen(false);
                setShowResolvedSnackbar(true);
            }
        } catch (e) {
            console.error(e);
        }
        setResolvingIncident(false);
    };

    const handleResolveIncident = async () => {
        setResolvingIncident(true);
        try {
            const res = await DATANETAPI.resolveTrackingIncident(currentDevice.id);
            await new Promise(resolve => setTimeout(resolve, 1500));
            if (res?.success) {
                setCurrentDevice(prev => ({
                    ...prev,
                    tracker_status:
                        res.tracker_status
                }));
                onTrackingStatusUpdated(
                    currentDevice.id,
                    res.tracker_status
                );
                setResolveModalOpen(false);
                setShowResolvedSnackbar(true);
            }
        } catch (e) {
            console.error(e);
        }
        setResolvingIncident(false);
    };

    useEffect(() => {
        setCurrentDevice(device);
    }, [device?.id]);

    const handleRefreshCurrentDevice = async ({ silent = false } = {}) => {
        if (!currentDevice?.id) {
            return null;
        }

        try {
            const snap = await DATANETAPI.getDevice(currentDevice.id);

            if (snap?.status < 0) {
                return null;
            }

            if (snap?.data !== undefined) {
                const refreshedDevice = Array.isArray(snap.data)
                    ? snap.data.find(d => d?.id === currentDevice.id) || snap.data[0]
                    : snap.data;

                if (!refreshedDevice?.id) {
                    return null;
                }

                const trackerStatusChanged =
                    refreshedDevice.tracker_status !== currentDevice?.tracker_status;

                const operationalStatusChanged =
                    refreshedDevice.operational_incident_status !== currentDevice?.operational_incident_status;

                const mergedDevice = {
                    ...currentDevice,
                    ...refreshedDevice,
                    tracker_operational_gis:
                        refreshedDevice.tracker_operational_gis ?? currentDevice?.tracker_operational_gis,
                    tracker_position:
                        refreshedDevice.tracker_position !== undefined
                            ? refreshedDevice.tracker_position
                            : currentDevice?.tracker_position,
                    tracker_assignments:
                        refreshedDevice.tracker_assignments ?? currentDevice?.tracker_assignments
                };

                setCurrentDevice(mergedDevice);

                if (trackerStatusChanged || operationalStatusChanged) {
                    onTrackingStatusUpdated?.(
                        mergedDevice.id,
                        mergedDevice.tracker_status,
                        mergedDevice.operational_incident_status
                    );
                }

                return mergedDevice;
            }
        } catch (e) {
            console.error(e);
        }

        return null;
    };


    return (
        <>
            <Dialog
                open={isStatusModalOpen}
                onClose={() => setisStatusModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        maxWidth: isSmallScreen ? undefined : 780,
                        width: isSmallScreen ? "calc(100% - 24px)" : "auto",
                        margin: isSmallScreen ? 12 : undefined,
                        borderRadius: isSmallScreen ? 8 : 8
                    }
                }}
            >
                <DialogTitle>{getDeviceDisplayName(currentDevice)}</DialogTitle>
                <DialogContent
                    style={{
                        padding: isSmallScreen ? "8px 16px 16px" : "8px 28px 20px"
                    }}
                >
                    {currentDevice?.status && currentDevice?.status?.device_status && currentDevice?.model == "Reanibex 100" ? (
                        <DeviceStatus
                            status={currentDevice?.status}
                            t={t}
                            trackerStatus={currentDevice?.tracker_status}
                            operationalIncidentStatus={currentDevice?.operational_incident_status}
                            hasComs={currentDevice?.connectivity}
                            software={currentDevice?.soft_version}
                            programType={currentDevice?.program_type}
                            trackerPosition={currentDevice?.tracker_position}
                            trackerOperationalGis={currentDevice?.tracker_operational_gis}
                            device={currentDevice}
                            onRefreshTracking={handleRefreshCurrentDevice}
                            autoOpenOperationalGis={autoOpenOperationalGis}
                            onOperationalGisAutoOpened={onOperationalGisAutoOpened}
                        />
                    ) : (
                        <DeviceStatusOthers device={currentDevice} />
                    )}
                </DialogContent>
                <DialogActions
                    style={{
                        padding: isSmallScreen ? "8px 16px 16px" : "8px 28px 20px",
                        flexDirection: isSmallScreen ? "column" : "row",
                        alignItems: isSmallScreen ? "stretch" : "center",
                        gap: isSmallScreen ? 8 : 0
                    }}
                >
                    {showResolveIncident && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<BuildIcon />}
                            onClick={() => setResolveModalOpen(true)}
                            style={{
                                fontWeight: 600,
                                borderWidth: 2,
                                width: isSmallScreen ? "100%" : undefined,
                                margin: isSmallScreen ? 0 : undefined
                            }}
                        >
                            {t(`${resolveActionI18nKey}.btn`)}
                        </Button>
                    )}
                    <Button
                        onClick={() => setisStatusModalOpen(false)}
                        color="primary"
                        autoFocus
                        variant="contained"
                        style={{
                            width: isSmallScreen ? "100%" : undefined,
                            margin: isSmallScreen ? 0 : undefined
                        }}
                    >
                        {t("common.ok")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={resolveModalOpen}
                onClose={() => setResolveModalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        margin: isSmallScreen ? 16 : undefined,
                        width: isSmallScreen ? "calc(100% - 32px)" : undefined
                    }
                }}
            >
                <DialogContent
                    style={{
                        paddingTop: isSmallScreen ? 22 : 28,
                        paddingLeft: isSmallScreen ? 16 : 24,
                        paddingRight: isSmallScreen ? 16 : 24
                    }}
                >
                    <Typography
                        variant={isSmallScreen ? "h5" : "h4"}
                        style={{
                            fontWeight: 400,
                            marginBottom: isSmallScreen ? 16 : 20
                        }}>
                        {t(`${resolveActionI18nKey}.title`)}
                    </Typography>
                    <DialogContentText
                        style={{
                            fontSize: 16,
                            color: "#424242",
                            lineHeight: 1.5,
                            marginBottom: 18
                        }}
                    >
                        {t(`${resolveActionI18nKey}.msg`)}
                    </DialogContentText>
                    <Typography style={{ color: "#9E9E9E", fontSize: 16 }}>
                        {t(`${resolveActionI18nKey}.warning`)}
                    </Typography>
                </DialogContent>
                <DialogActions
                    style={{
                        padding: isSmallScreen ? "12px 16px 20px" : "16px 24px 24px",
                        flexDirection: isSmallScreen ? "column-reverse" : "row",
                        alignItems: isSmallScreen ? "stretch" : "center",
                        gap: isSmallScreen ? 8 : 0
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => setResolveModalOpen(false)}
                        style={{
                            width: isSmallScreen ? "100%" : undefined,
                            margin: isSmallScreen ? 0 : undefined
                        }}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        disabled={resolvingIncident}
                        onClick={showOperationalClosure ? handleConfirmOperationalClosure : handleResolveIncident}
                        style={{
                            width: isSmallScreen ? "100%" : undefined,
                            margin: isSmallScreen ? 0 : undefined
                        }}
                    >
                        {resolvingIncident ? (
                            <CircularProgress size={22} thickness={5} style={{ color: "white" }} />
                        ) : (
                            t(`${resolveActionI18nKey}.btn_confirm`)
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={showResolvedSnackbar}
                autoHideDuration={4000}
                onClose={() => setShowResolvedSnackbar(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <MuiAlert
                    elevation={6}
                    variant="filled"
                    severity="success"
                    onClose={() => setShowResolvedSnackbar(false)}
                >
                    {t(`${resolveActionI18nKey}.success`)}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

export default DeviceStatusModal;
